import React from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { styled } from '@spazious/react-styles';
dayjs.extend(relativeTime);
dayjs.extend(duration);

export type DurationFieldProps<P = any> = Omit<P, 'value' | 'onChange'> & {
  /**
   * The units to display in the duration field (e.g. 'yMdhms')
   */
  units?: string;
  /**
   * The value of the field in ISO 8601 duration format
   */
  value?: string | null;
  /**
   * When true, the component will preserve zeroes in the duration string
   */
  preserveZeroes?: boolean;
  /**
   * The onChange handler for the field
   */
  inputComponent?: React.ComponentType<P>;
};

export const DefaultInputComponent = styled.input`
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #e0e0e0;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  color: var(--primary-text);

  &:hover {
    background-color: #f5f5f5;
  }
`;

/**
 * Duration field
 * @param {DurationFieldProps} props
 */
export const DurationField = React.forwardRef(
  <P = any,>(
    {
      value,
      inputComponent: InputComponent = DefaultInputComponent as React.ComponentType<P>,
      preserveZeroes = false,
      units = 'yMdhms',
      ...restProps
    }: DurationFieldProps<P>,
    ref: React.ForwardedRef<any>
  ) => {
    const parsedDuration = value ? dayjs.duration(value) : null;
    const formattedDuration = parsedDuration
      ? (Array.from(units) as duration.DurationUnitType[]).reduce((acc, unitKey) => {
          const unitValue = parsedDuration.get(unitKey);
          if (!preserveZeroes && unitValue === 0) {
            return acc;
          }
          return `${acc}${!acc ? '' : ' '}${unitValue}${unitKey}`;
        }, '')
      : '';
    return (
      <InputComponent
        ref={ref}
        classname="customDurationInput"
        value={formattedDuration}
        {...(restProps as any)}
      />
    );
  }
);

export default DurationField;
